import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { ArrowRight, Loader2, Clock, CalendarDays, Info, ArrowLeft } from "lucide-react";
import axios from "axios";
import { format } from "date-fns";
import { useAuth } from "@/context/AuthContext";
import { formatTime12Hour } from "@/utils/timeUtils";
import { DEFAULT_MEETING_TYPE } from "@/constants/meetingTypes";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_aianalytics-4/artifacts/d89kgck4_A_I_Means_Business.jpg";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SchedulePage = () => {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [lead, setLead] = useState(null);
  const [evaluation, setEvaluation] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [unavailability, setUnavailability] = useState({ weekly: [], specific_dates: [] });
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState(60);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dynamicPricing, setDynamicPricing] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [leadRes, slotsRes, evalRes, unavailRes, pricingRes] = await Promise.all([
          axios.get(`${API}/public/lead/${leadId}`),
          axios.get(`${API}/public/available-slots`),
          axios.get(`${API}/public/evaluation/${leadId}`).catch(() => null),
          axios.get(`${API}/scheduling/availability-config`).catch(() => ({ data: { weekly: [], specific_dates: [] } })),
          axios.get(`${API}/public/service-prices`).catch(() => null)
        ]);
        setLead(leadRes.data);
        setAvailableSlots(slotsRes.data);
        if (evalRes?.data) {
          setEvaluation(evalRes.data);
        }
        if (unavailRes?.data) {
          setUnavailability(unavailRes.data);
        }
        if (pricingRes?.data) {
          setDynamicPricing(pricingRes.data);
        }
      } catch (error) {
        toast.error("Unable to load scheduling information");
        navigate("/portal");
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [leadId, navigate]);

  // Get dynamic price for standard tier (Service A)
  const getStandardPrice = () => {
    if (dynamicPricing?.SERVICE_A?.display) {
      return dynamicPricing.SERVICE_A.display;
    }
    return "$997";  // Fallback
  };

  // Get contextual service from evaluation report, or use default
  const recommendedService = evaluation?.section_8_next_step?.recommended_service || {
    name: "Discovery & Viability Assessment",
    price: getStandardPrice(),
    tier: "standard",
    description: "A structured assessment to clarify opportunities, identify risks, and develop a clear picture of what A.I. adoption could—and should—look like for your specific business."
  };

  const availableDates = [...new Set(availableSlots.map(slot => slot.date))];
  
  const timesForSelectedDate = selectedDate
    ? availableSlots.filter(slot => slot.date === format(selectedDate, "yyyy-MM-dd"))
    : [];

  // Check if a date is disabled based on admin unavailability rules
  const isDateDisabled = (date) => {
    // Past dates are disabled
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) {
      return true;
    }

    // Check weekly unavailability (0 = Sunday, 1 = Monday, etc.)
    const dayOfWeek = date.getDay();
    if (unavailability.weekly && unavailability.weekly.includes(dayOfWeek)) {
      return true;
    }

    // Check specific date blockouts
    if (unavailability.specific_dates && unavailability.specific_dates.length > 0) {
      const dateStr = format(date, "yyyy-MM-dd");
      for (const blockout of unavailability.specific_dates) {
        const startDate = new Date(blockout.start_date);
        const endDate = new Date(blockout.end_date);
        const checkDate = new Date(dateStr);
        
        // Normalize to compare dates only
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(0, 0, 0, 0);
        checkDate.setHours(0, 0, 0, 0);
        
        if (checkDate >= startDate && checkDate <= endDate) {
          return true;
        }
      }
    }

    return false;
  };

  const handleSchedule = async () => {
    if (!selectedDate || !selectedTime) {
      toast.error("Please select a date and time");
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/public/schedule`, {
        lead_id: leadId,
        date: format(selectedDate, "yyyy-MM-dd"),
        time: selectedTime,
        duration: selectedDuration,
        service_tier: recommendedService?.tier || "standard",
        meeting_type: DEFAULT_MEETING_TYPE  // Users get default "Courtesy" type, admin can change later
      });

      toast.success("Appointment scheduled successfully!");
      
      // Redirect to user portal after short delay to show success message
      setTimeout(() => {
        navigate("/portal");
      }, 1500);
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to schedule appointment";
      toast.error(message);
      setIsSubmitting(false);
    }
  };

  const isDateAvailable = (date) => {
    // First check if admin has marked this date as unavailable
    if (isDateDisabled(date)) {
      return false;
    }
    // Then check if there are available slots for this date
    const dateStr = format(date, "yyyy-MM-dd");
    return availableDates.includes(dateStr);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      {/* Authenticated Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-navy-950/95 backdrop-blur border-b border-slate-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <Link to="/portal" className="flex items-center gap-2 flex-shrink-0">
            <img 
              src={LOGO_URL}
              alt="A.I. Means Business"
              className="h-12 sm:h-16 md:h-20 w-auto"
            />
          </Link>
          <Link to="/portal">
            <Button
              variant="outline"
              size="sm"
              className="border-slate-700 text-slate-300 hover:text-white text-xs sm:text-sm"
              data-testid="back-to-portal-btn"
            >
              <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Back to Portal</span>
              <span className="sm:hidden">Back</span>
            </Button>
          </Link>
        </div>
      </header>

      {/* Main content with top padding for fixed header */}
      <section className="pt-20 sm:pt-24 md:pt-28 py-6 sm:py-12 bg-gradient-to-b from-navy-950 to-navy-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          {/* Page Title */}
          <div className="mb-4 sm:mb-6">
            <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-white tracking-tight break-words">
              A.I. Means Business
            </h1>
            <h2 className="font-heading text-xl sm:text-2xl md:text-3xl font-bold text-primary mt-1">
              Schedule Your Session
            </h2>
          </div>
          
          <div className="text-left mb-6 sm:mb-10">
            <p className="text-slate-300 text-sm sm:text-base">
              Welcome, {lead?.first_name || lead?.contact_name}! Select your preferred date and time for your advisory session.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-8">
            {/* Calendar */}
            <div className="glass p-4 sm:p-6 rounded-2xl" data-testid="schedule-calendar">
              <h3 className="font-heading font-semibold text-white mb-4 flex items-center gap-2 text-sm sm:text-base">
                <CalendarDays className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                Select a Date
              </h3>
              {availableDates.length > 0 ? (
                <div className="calendar-wrapper overflow-x-auto">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    disabled={(date) => !isDateAvailable(date)}
                    className="rounded-md bg-navy-900 w-full min-w-[280px]"
                    classNames={{
                      months: "flex flex-col space-y-4 justify-center",
                      month: "space-y-4 w-full",
                      caption: "flex justify-center pt-1 relative items-center mb-4",
                      caption_label: "text-sm sm:text-base font-heading font-semibold text-white",
                      nav: "space-x-1 flex items-center",
                      nav_button: "h-7 w-7 sm:h-8 sm:w-8 bg-navy-800 hover:bg-primary/20 rounded-md flex items-center justify-center text-white border border-slate-700",
                      nav_button_previous: "absolute left-1",
                      nav_button_next: "absolute right-1",
                      table: "w-full border-collapse",
                      head_row: "flex w-full justify-between mb-2",
                      head_cell: "text-slate-400 font-medium text-xs sm:text-sm w-8 sm:w-10 text-center",
                      row: "flex w-full justify-between mt-1",
                      cell: "relative p-0 text-center focus-within:relative focus-within:z-20 h-8 w-8 sm:h-10 sm:w-10",
                      day: "h-8 w-8 sm:h-10 sm:w-10 p-0 font-normal text-white hover:bg-primary/20 rounded-md flex items-center justify-center transition-colors text-xs sm:text-sm",
                      day_selected: "bg-primary text-white hover:bg-primary hover:text-white font-semibold",
                      day_today: "bg-navy-800 text-white font-medium border border-slate-600",
                      day_outside: "text-slate-600 opacity-50",
                      day_disabled: "text-slate-700 opacity-30 cursor-not-allowed hover:bg-transparent",
                      day_hidden: "invisible",
                    }}
                  />
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-slate-400">No available dates at the moment.</p>
                  <p className="text-sm text-slate-500 mt-2">Please contact us to arrange a session.</p>
                </div>
              )}
            </div>

            {/* Time Slots */}
            <div className="glass p-4 sm:p-6 rounded-2xl" data-testid="schedule-times">
              <h3 className="font-heading font-semibold text-white mb-4 flex items-center gap-2 text-sm sm:text-base">
                <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                Select a Time
              </h3>
              
              {selectedDate ? (
                <>
                  <p className="text-slate-400 text-xs sm:text-sm mb-4">
                    Available times for {format(selectedDate, "MMMM d, yyyy")}
                  </p>
                  
                  {/* Duration Selection */}
                  <div className="mb-4">
                    <p className="text-xs sm:text-sm text-slate-400 mb-2">Session Duration</p>
                    <div className="flex gap-2">
                      <Button
                        variant={selectedDuration === 30 ? "default" : "outline"}
                        size="sm"
                        onClick={() => setSelectedDuration(30)}
                        className={`text-xs sm:text-sm ${selectedDuration === 30 
                          ? "bg-primary text-white" 
                          : "border-slate-700 text-slate-300 hover:text-white"
                        }`}
                      >
                        30 min
                      </Button>
                      <Button
                        variant={selectedDuration === 60 ? "default" : "outline"}
                        size="sm"
                        onClick={() => setSelectedDuration(60)}
                        className={`text-xs sm:text-sm ${selectedDuration === 60 
                          ? "bg-primary text-white" 
                          : "border-slate-700 text-slate-300 hover:text-white"
                        }`}
                      >
                        60 min
                      </Button>
                    </div>
                  </div>

                  {/* Time Grid */}
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5 sm:gap-2 max-h-48 sm:max-h-64 overflow-y-auto">
                    {timesForSelectedDate.map((slot, index) => (
                      <Button
                        key={index}
                        variant={selectedTime === slot.time ? "default" : "outline"}
                        size="sm"
                        onClick={() => setSelectedTime(slot.time)}
                        className={`text-xs sm:text-sm px-2 sm:px-3 ${
                          selectedTime === slot.time
                            ? "bg-primary text-white"
                            : "border-slate-700 text-slate-300 hover:text-white hover:border-primary/50"
                        }`}
                        data-testid={`time-slot-${slot.time}`}
                      >
                        {formatTime12Hour(slot.time)}
                      </Button>
                    ))}
                  </div>

                  {timesForSelectedDate.length === 0 && (
                    <p className="text-slate-500 text-center py-4 text-sm">
                      No available times for this date
                    </p>
                  )}
                </>
              ) : (
                <div className="text-center py-8">
                  <p className="text-slate-400 text-sm">Select a date to see available times</p>
                </div>
              )}
            </div>
          </div>

          {/* Schedule Button */}
          <div className="mt-6 sm:mt-8 glass p-4 sm:p-6 rounded-2xl">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex-1 text-center sm:text-left">
                <p className="text-slate-300 text-sm sm:text-base">
                  {selectedDate && selectedTime ? (
                    <>
                      <span className="text-white font-medium">{format(selectedDate, "MMMM d, yyyy")}</span>
                      {" at "}
                      <span className="text-white font-medium">{formatTime12Hour(selectedTime)}</span>
                      {" • "}
                      <span className="text-slate-400">{selectedDuration} min session</span>
                    </>
                  ) : (
                    "Select your preferred date and time above"
                  )}
                </p>
              </div>
              <Button
                onClick={handleSchedule}
                disabled={!selectedDate || !selectedTime || isSubmitting}
                className="bg-primary hover:bg-primary/90 text-white font-bold rounded-full px-6 sm:px-8 py-3 sm:py-4 disabled:opacity-50 text-sm sm:text-base w-full sm:w-auto"
                data-testid="schedule-submit"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                    Scheduling...
                  </>
                ) : (
                  <>
                    Schedule Session
                    <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-800 py-6 sm:py-8 mt-8 sm:mt-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <Link to="/portal" className="inline-block mb-4">
            <img 
              src={LOGO_URL}
              alt="A.I. Means Business"
              className="h-12 sm:h-16 w-auto mx-auto"
            />
          </Link>
          <p className="text-slate-500 text-xs">
            Copyright © 2026, Use What You Got Publishing, LLC • www.AIMeansBusiness.net
          </p>
        </div>
      </footer>
    </div>
  );
};

export default SchedulePage;
